/**
 * Version History Viewer Component
 * Shows edit history for any CMS entity
 */

import { useState, useEffect } from 'react';
import { Clock, User, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import versionHistoryService from '../../services/versionHistoryService';
import type { VersionHistoryEntry } from '../../types/versionHistory';

interface VersionHistoryViewerProps {
  entityType: string;
  entityId: string | number;
  isOpen: boolean;
  onClose: () => void;
}

export function VersionHistoryViewer({
  entityType,
  entityId,
  isOpen,
  onClose,
}: VersionHistoryViewerProps) {
  const [history, setHistory] = useState<VersionHistoryEntry[]>([]);

  useEffect(() => {
    if (isOpen) {
      const entries = versionHistoryService.getHistory(entityType, entityId);
      setHistory(entries);
    }
  }, [isOpen, entityType, entityId]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'bg-green-100 text-green-800';
      case 'update':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Edit History</DialogTitle>
          <DialogDescription>
            All changes made to this {entityType.replace('-', ' ')}
          </DialogDescription>
        </DialogHeader>

        <div className="h-[500px] overflow-y-auto pr-4">
          {history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No edit history available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={getActionColor(entry.action)}>
                        {entry.action.toUpperCase()}
                      </Badge>
                      <span className="text-sm font-medium">{entry.changes}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {entry.user}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTimestamp(entry.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
